import type { Person, Problem } from "@pagerless/schema";
import { type ReactNode, useEffect, useRef } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useOutletContext } from "react-router";
import { ApiError } from "../api";
import { useDeps, useResource } from "../context";
import { chipStatus } from "../lib/pager";
import { browserTimeZone, zoneToSave } from "../lib/timeZone";
import { PagerChip } from "./Lcd";
import { Wordmark } from "./Wordmark";

export function Loading({ what = "Loading" }: { what?: string }) {
  return (
    <p className="skeleton" role="status">
      {what}…
    </p>
  );
}

export function ErrorNote({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <div className="err" role="alert">
      {error instanceof ApiError ? error.message : "Something went wrong. Try again."}{" "}
      {retry && (
        <button type="button" className="link-btn" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

const NAV: Array<{ to: string; label: string }> = [
  { to: "/home", label: "Home" },
  { to: "/problems", label: "Problems" },
  { to: "/apps", label: "Apps" },
  { to: "/rules", label: "Helper rules" },
  { to: "/people", label: "People" },
  { to: "/alerts", label: "Alerts" },
  { to: "/plan", label: "Plan" },
];

export type ShellContext = { refreshBadge: () => void };

/** For pages inside the shell: refresh the "needs you" badge after a change. Safe outside the shell. */
export function useShell(): ShellContext {
  return useOutletContext<ShellContext | undefined>() ?? { refreshBadge: () => {} };
}

/** Favicon dot: the tab shows the pager light, so a background tab turns red when you're needed. Unlit until known. */
function useStatusFavicon(needed: boolean | null) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const color = needed === null ? "#4a4f54" : needed ? "#ff4b2b" : "#7bc86c";
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect x="3" y="7" width="26" height="18" rx="6" fill="#1e2022"/><rect x="7" y="11" width="13" height="10" rx="2" fill="#b5c994"/><circle cx="24" cy="16" r="3" fill="${color}"/></svg>`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [needed]);
}

/**
 * Once per signed-in session: an owner's browser sets the account time zone when the account has none
 * or a different one, so alerts and summaries use the owner's local times. Members never do; a 403 is
 * expected then and ignored. Other failures are logged, never shown: nothing on screen depends on it.
 */
function useAccountTimeZone(role: Person["role"] | null) {
  const { api, timeZone } = useDeps();
  const done = useRef(false);
  useEffect(() => {
    if (role === null || done.current) return;
    done.current = true;
    if (role !== "owner") return;
    void (async () => {
      try {
        const zone = zoneToSave({
          role,
          accountZone: (await api.settings()).timeZone,
          browserZone: (timeZone ?? browserTimeZone)(),
        });
        if (zone) await api.setTimeZone(zone);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) return;
        console.warn("Couldn't set the account time zone", e);
      }
    })();
  }, [api, timeZone, role]);
}

/** Signed-in layout: a paper top bar; the pager chip is the only status display (design v2). */
export function Shell() {
  const location = useLocation();
  const me = useResource((api) => api.me());
  // All problems, not just open ones: a fix still running or being checked is not "all good" (bug f).
  const problems = useResource((api) => api.problems(), location.pathname);
  const navRef = useRef<HTMLElement>(null);

  // On phones the nav scrolls sideways; keep the current page in view.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on navigation.
  useEffect(() => {
    navRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({
      block: "nearest",
      inline: "nearest",
    });
  }, [location.pathname]);

  // The last list we actually loaded: a reload on navigation keeps showing it; before the first load, nothing (E1).
  const known = useRef<Problem[] | null>(null);
  if (problems.status === "ok") known.current = problems.data;
  const list = known.current;
  const openCount = list?.filter((p) => p.state === "open").length ?? 0;
  const chip = chipStatus(list);
  // Hooks before any early return.
  useStatusFavicon(list === null ? null : openCount > 0);
  useAccountTimeZone(me.status === "ok" ? me.data.person.role : null);

  if (me.status === "error" && me.error instanceof ApiError && me.error.status === 401) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  const initial = me.status === "ok" ? me.data.person.name.slice(0, 1).toUpperCase() : "";

  return (
    <div className="app">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="topbar2">
        <Link to="/home" className="logo" aria-label="Pagerless home">
          <Wordmark />
        </Link>
        <nav aria-label="Main" ref={navRef}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <Link
          to={chip.tone === "green" || list === null ? "/home" : "/problems"}
          className="pchip-link"
          aria-label={
            list === null
              ? "Status: loading"
              : openCount > 0
                ? `Problems, ${openCount} open`
                : chip.tone === "green"
                  ? "Status: all good"
                  : `Status: ${chip.text.toLowerCase()}`
          }
        >
          <PagerChip tone={chip.tone} text={chip.text} />
        </Link>
        <span className="avatar" title={me.status === "ok" ? me.data.person.name : undefined} aria-hidden="true">
          {initial}
        </span>
      </header>
      <main id="main" className="main" tabIndex={-1}>
        {me.status === "error" && <ErrorNote error={me.error} retry={me.reload} />}
        <Outlet context={{ refreshBadge: problems.reload } satisfies ShellContext} />
      </main>
    </div>
  );
}

export function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <h1 className="h2">{title}</h1>
      {children}
    </>
  );
}
